import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Popover from '@welldev-group/rcl-popover';
import IconButton from '@welldev-group/rcl-icon-button';
import { I18nText, Link, Spinner } from '@welldev-group/react-comp-lib';
import Icon from '@welldev-group/rcl-icon';
import ConfirmationModal from '../../../shared/components/ConfirmationModal';
import deactivateAffiliate from '../AffiliateThreeSixty/AffiliateList/helpers/api';
import {
  WARNING,
  SUCCESS,
  DANGER,
} from '../../../shared/constants/commonStates';
import SanitizedHTML from '../../../shared/components/SanitizedHTML';

const ShowMoreAffiliates = ({
  deactivatedAffiliates,
  showDetailsUrl,
  affiliateId,
  affiliateName,
  onConfirmationModalDismiss,
  plan,
  subscriptionPeriod,
  workspaces,
  activeUsers,
  totalChannels,
}) => {
  const [isDeactivationInProgress, setIsDeactivationInProgress] = useState(
    false,
  );
  const [confirmDeactivation, setConfirmDeactivation] = useState(false);
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  let timeoutKey = null;

  const onCloseConfirmationModal = () => {
    setConfirmationModalOpen(false);
  };

  const onOpenConfirmationModal = () => {
    setConfirmationModalOpen(true);
  };

  const onClosePopover = () => {
    setIsPopoverVisible(false);
  };

  const onTogglePopover = () => {
    setIsPopoverVisible(prev => !prev);
  };

  const handleDeactivationClick = () => {
    setConfirmDeactivation(true);
    onClosePopover();
    onOpenConfirmationModal();
  };

  const showMoreComponentOptions = () => {
    const options = [
      {
        link: showDetailsUrl,
        label: (
          <I18nText id="affiliate_three_sixty.affiliate_list.show_details" />
        ),
        withOnClickFunction: false,
      },
    ];

    if (!deactivatedAffiliates) {
      options.push({
        item: (
          /* eslint-disable-next-line jsx-a11y/anchor-is-valid */
          <Link
            className="show-more__content"
            modifier="default"
            onClick={handleDeactivationClick}
          >
            <I18nText id="affiliate_three_sixty.affiliate_list.deactivate" />
          </Link>
        ),
        key: `deactivate-${affiliateId}`,
      });
    }

    return options;
  };

  const getPopoverContent = () => {
    const list = [];
    showMoreComponentOptions().forEach(option => {
      let { item, key } = option;
      if (!item) {
        key = option.link;
        item = (
          <Link
            modifier="default"
            className="show-more__content"
            href={option.link}
          >
            {option.label}
          </Link>
        );
      }

      list.push(
        <li key={key} className="show-more__item">
          {item}
        </li>,
      );
    });

    return <ul className="show-more__list">{list}</ul>;
  };

  const getModalContent = () => {
    if (isDeactivationInProgress) {
      return (
        <div className="affiliate-deactivate-loader__wrapper">
          <Spinner bgColor="neutral" size="small" color="success" />
          <p className="affiliate-deactivate-loader__text">
            {I18n.t(
              'components.affiliate_three_sixty.affiliate.deactivate.confirmation_modal.success_subheader',
              {
                affiliate_name: affiliateName,
              },
            )}
          </p>
        </div>
      );
    }

    if (confirmDeactivation) {
      return I18n.t(
        'components.affiliate_three_sixty.affiliate.deactivate.confirmation_modal.confirmation_text',
        {
          affiliate_name: affiliateName,
        },
      );
    }

    if (isSuccess) {
      return I18n.t(
        'components.affiliate_three_sixty.affiliate.deactivate.confirmation_modal.deactivation_successful_message',
        {
          affiliate_name: affiliateName,
        },
      );
    }

    return I18n.t(
      'components.affiliate_three_sixty.affiliate.deactivate.confirmation_modal.deactivation_failed_message',
      {
        affiliate_name: affiliateName,
      },
    );
  };

  const getIconType = () => {
    if (isDeactivationInProgress) {
      return null;
    }

    if (confirmDeactivation) {
      return WARNING;
    }

    if (isSuccess) {
      return SUCCESS;
    }

    return DANGER;
  };

  const initiateAffiliateDeactivation = async affiliateId => {
    setIsDeactivationInProgress(true);

    try {
      const data = await deactivateAffiliate(affiliateId);

      if (data.error === null) {
        setIsSuccess(true);
      }
    } catch (error) {
      setIsSuccess(false);
    } finally {
      setIsDeactivationInProgress(false);
    }
  };

  const onConfirmDeactivation = () => {
    setConfirmDeactivation(false);
    initiateAffiliateDeactivation(affiliateId);
  };

  const handleConfirmationModalDismiss = () => {
    if (confirmationModalOpen) {
      onCloseConfirmationModal();
      onConfirmationModalDismiss();
    }
  };

  useEffect(() => {
    if (!(confirmDeactivation || isDeactivationInProgress)) {
      timeoutKey = setTimeout(handleConfirmationModalDismiss, 3000);
    }

    return () => {
      clearTimeout(timeoutKey);
    };
  }, [confirmDeactivation, isDeactivationInProgress]);

  return (
    <div className="show-more">
      <Popover
        className="settings__sections-header-locale-wrapper-popover"
        isVisible={isPopoverVisible}
        onOutsideClick={onClosePopover}
        content={getPopoverContent()}
        size="small"
        direction="bottom-right"
      >
        <IconButton
          icon={<Icon name="horizontalDots" size="normal" />}
          onClick={onTogglePopover}
          label={<I18nText id="show_more.more" />}
        />
      </Popover>

      <ConfirmationModal
        type={getIconType()}
        onOutsideClick={onCloseConfirmationModal}
        onConfirm={onConfirmDeactivation}
        onReject={onCloseConfirmationModal}
        isModalOpen={confirmationModalOpen}
        withAction={confirmDeactivation}
        confirmationHeader={getModalContent()}
        subHeader={
          confirmDeactivation && (
            <SanitizedHTML
              html={I18n.t(
                'components.affiliate_three_sixty.affiliate.deactivate.confirmation_modal.confirmation_details',
                {
                  affiliate_name: affiliateName,
                  plan,
                  subscriptionPeriod,
                  workspaces,
                  activeUsers,
                  totalChannels,
                },
              )}
            />
          )
        }
      />
    </div>
  );
};

ShowMoreAffiliates.defaultProps = {
  plan: '',
  subscriptionPeriod: '',
  totalChannels: 0,
};

ShowMoreAffiliates.propTypes = {
  deactivatedAffiliates: PropTypes.bool.isRequired,
  showDetailsUrl: PropTypes.string.isRequired,
  affiliateId: PropTypes.number.isRequired,
  affiliateName: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
    .isRequired,
  onConfirmationModalDismiss: PropTypes.func.isRequired,
  plan: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  subscriptionPeriod: PropTypes.string,
  workspaces: PropTypes.oneOfType([PropTypes.node, PropTypes.number])
    .isRequired,
  activeUsers: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
    .isRequired,
  totalChannels: PropTypes.number,
};

export default ShowMoreAffiliates;
